import { WorkOrderDialog } from "./counter/WorkOrderDialog";
import { ClipboardList } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Link, useSearchParams } from "react-router-dom";
import { FileDown, Loader2, Type, Shapes, Square, Circle, Save, FolderOpen, Ruler } from "lucide-react";
import { api, downloadFile, errMsg, eur } from "@/lib/api";
import { useFontsTick } from "@/lib/meta";
import { computeLayout, initialDesign, cleanDesign } from "@/lib/layout";
import { StampCanvas } from "@/components/editor/StampCanvas";
import { TextLinesPanel } from "@/components/editor/TextLinesPanel";
import { GraphicsPanel } from "@/components/editor/GraphicsPanel";

const PRESETS = [[27, 10], [38, 14], [47, 18], [59, 23]];
const line = (text, size, bold = false) => ({ text, font: "arimo", size_pt: size, bold, italic: false, underline: false, align: "center", curve: null, offset_x: 0, offset_y: 0, letter_spacing_mm: 0 });
const DEFAULT_LINES = [line("RAGIONE SOCIALE", 10, true), line("Via Roma 1 - 00100 Città", 7.5), line("P.IVA 01234567890", 7.5)];
const cfgFor = (round) => ({
  max_lines: 99, customer_can_add_lines: true, default_lines: DEFAULT_LINES, min_font_pt: 3, max_font_pt: 96, default_font: "arimo", allowed_fonts: [],
  allow_logo: true, allow_clipart: true, allow_qr: true, max_graphics: 20, allow_border: true, allow_curved_text: round, margin_mm: 1, extra_line_price: 0,
});
const label = (s) => (s.shape === "round" ? `Ø ${s.w} mm` : `${s.h}×${s.w} mm`);
const num = (v) => Math.round(parseFloat(String(v).replace(",", ".")) * 10) / 10;

function SizeForm({ size, onApply }) {
  const [shape, setShape] = useState(size.shape);
  const [w, setW] = useState(size.w);
  const [h, setH] = useState(size.h);
  useEffect(() => { setShape(size.shape); setW(size.w); setH(size.h); }, [size]);
  const apply = () => {
    const W = num(w), H = shape === "round" ? W : num(h);
    if (!(W > 3 && W <= 300 && H > 3 && H <= 300)) return toast.error("Misure tra 4 e 300 mm");
    onApply({ shape, w: W, h: H });
  };
  const sb = (s, Icon, t) => (
    <button data-testid={`free-shape-${s}`} onClick={() => setShape(s)} className={`press h-9 px-3 rounded-md border text-sm flex items-center gap-1.5 ${shape === s ? "bg-brand-navy text-white border-brand-navy" : "bg-white"}`}><Icon className="w-4 h-4" /> {t}</button>
  );
  const box = "h-9 w-20 px-2 rounded-md border border-slate-200 text-sm font-mono";
  return (
    <div className="flex flex-wrap items-end gap-2" data-testid="free-size-form">
      {sb("rect", Square, "Rettangolare")}{sb("round", Circle, "Rotondo")}
      {shape === "round" ? (
        <label className="text-[11px] text-slate-500">Diametro mm<input data-testid="free-diameter-input" className={box + " block"} value={w} onChange={(e) => setW(e.target.value)} /></label>
      ) : (
        <>
          <label className="text-[11px] text-slate-500">Altezza mm<input data-testid="free-height-input" className={box + " block"} value={h} onChange={(e) => setH(e.target.value)} /></label>
          <label className="text-[11px] text-slate-500">Larghezza mm<input data-testid="free-width-input" className={box + " block"} value={w} onChange={(e) => setW(e.target.value)} /></label>
        </>
      )}
      <button data-testid="free-size-apply-btn" onClick={apply} className="press h-9 px-4 rounded-md bg-brand-yellow text-brand-navy text-sm font-bold">Crea editor</button>
    </div>
  );
}

const sameSize = (p, size) => {
  const round = p.shape === "round" || p.shape === "oval";
  if ((size.shape === "round") !== round) return false;
  const w = Math.max(p.width_mm, p.height_mm), h = round ? w : Math.min(p.width_mm, p.height_mm);
  return w === size.w && h === size.h;
};
const isGom = (p) => p.category === "gommini" || (p.name || "").toLowerCase().startsWith("gommino");

function CatalogSizes({ size, onPick, products }) {
  const [sizes, setSizes] = useState(null);
  useEffect(() => {
    if (!products) return;
    {
      const m = {};
      products.filter((p) => p.kind !== "pad" && p.width_mm && p.height_mm).forEach((p) => {
        const round = p.shape === "round" || p.shape === "oval";
        const s = { shape: round ? "round" : "rect", w: Math.max(p.width_mm, p.height_mm), h: Math.min(p.width_mm, p.height_mm) };
        if (round) s.h = s.w;
        const k = `${s.shape}-${s.h}x${s.w}`;
        m[k] = m[k] || { ...s, k, names: [] };
        m[k].names.push(p.name);
      });
      setSizes(Object.values(m).sort((a, b) => a.shape.localeCompare(b.shape) || a.h - b.h || a.w - b.w));
    }
  }, [products]);
  return (
    <div data-testid="catalog-sizes">
      <div className="font-mono text-[11px] uppercase text-slate-500 mb-2">Misure a catalogo {sizes && `(${sizes.length})`}</div>
      {!sizes && <p className="text-xs text-slate-500">Caricamento…</p>}
      <div className="space-y-1">
        {sizes?.map((s) => {
          const on = s.shape === size.shape && s.w === size.w && s.h === size.h;
          return (
            <button key={s.k} data-testid={`catalog-size-${s.k}`} onClick={() => onPick(s)} title={s.names.join(", ")}
              className={`press w-full text-left rounded-md px-2.5 py-1.5 border text-sm ${on ? "bg-brand-navy text-white border-brand-navy" : "bg-white hover:border-brand-navy border-slate-200"}`}>
              <span className="font-mono font-semibold">{label(s)}</span>
              <span className={`block text-[10px] truncate ${on ? "text-white/70" : "text-slate-500"}`}>{s.names.length} prodott{s.names.length > 1 ? "i" : "o"} · {s.names[0]}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export const freeProduct = (size) => ({ id: "free", name: "Timbro libero", shape: size.shape, width_mm: size.w, height_mm: size.h, ink_colors: ["black"], config: cfgFor(size.shape === "round") });
export const sizeLabel = label;

function SaveBar({ loaded, onSave, matches }) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [pid, setPid] = useState("");
  const [qty, setQty] = useState(1);
  useEffect(() => { if (loaded) { setName(loaded.name); setQty(loaded.quantity || 1); } }, [loaded]);
  useEffect(() => { setPid((cur) => (matches.some((p) => p.id === cur) ? cur : loaded?.product_id && matches.some((p) => p.id === loaded.product_id) ? loaded.product_id : matches[0]?.id || "")); }, [matches, loaded]);
  const save = async () => {
    if (!name.trim()) return toast.error("Dai un nome al timbro");
    setBusy(true);
    try { await onSave(name.trim(), pid || null, Math.max(1, +qty || 1)); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-xl border-2 border-brand-navy bg-white p-3 flex flex-wrap items-center gap-2" data-testid="free-save-bar">
      <Save className="w-5 h-5 text-brand-navy" />
      <input data-testid="free-save-name-input" value={name} maxLength={120} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && save()}
        placeholder="Nome del timbro (es. Studio Rossi - indirizzo)" className="flex-1 min-w-[200px] h-10 px-3 rounded-md border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-brand-yellow/70" />
      <button data-testid="free-save-btn" disabled={busy} onClick={save} className="press h-10 px-5 rounded-md bg-brand-navy text-white text-sm font-bold flex items-center gap-2 disabled:opacity-60">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Salva timbro
      </button>
      <Link to="/admin/timbri-salvati" data-testid="free-saved-link" className="press h-10 px-4 rounded-md border border-brand-navy text-brand-navy text-sm font-semibold flex items-center gap-2"><FolderOpen className="w-4 h-4" /> Timbri salvati</Link>
      <div className="basis-full flex flex-wrap items-center gap-2 text-xs" data-testid="free-save-product-row">
        {matches.length ? (
          <>
            <span className="text-slate-600">Statistiche negozio: venduto come</span>
            <select data-testid="free-save-product-select" value={pid} onChange={(e) => setPid(e.target.value)} className="h-8 px-2 rounded border border-slate-200 bg-white text-xs max-w-[320px]">
              {matches.map((p) => <option key={p.id} value={p.id}>{p.name}{p.sku ? ` (${p.sku})` : ""} · {eur(p.price)}</option>)}
            </select>
            <span className="text-slate-600">pezzi</span>
            <input data-testid="free-save-qty" type="number" min="1" value={qty} onChange={(e) => setQty(e.target.value)} className="h-8 w-16 px-2 rounded border border-slate-200 text-xs" />
          </>
        ) : <span className="text-amber-700" data-testid="free-save-no-match">Misura non presente a catalogo: il timbro non verrà conteggiato nelle statistiche negozio.</span>}
      </div>
      {loaded && <span className="basis-full text-[11px] text-slate-500" data-testid="free-loaded-name">Aperto: <b>{loaded.name}</b> · salvando si crea sempre una nuova copia</span>}
    </div>
  );
}

export function AdminStampCreator() {
  const [params] = useSearchParams();
  const sid = params.get("id");
  const [size, setSize] = useState({ shape: "rect", w: 38, h: 14 });
  const round = size.shape === "round";
  const product = useMemo(() => freeProduct(size), [size]);
  const [design, setDesign] = useState(() => initialDesign(product));
  const [loaded, setLoaded] = useState(null);
  const [selected, setSelected] = useState(null);
  const [warnings, setWarnings] = useState([]);
  const [tab, setTab] = useState("text");
  const [busy, setBusy] = useState("");
  const fontsTick = useFontsTick();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const layout = useMemo(() => computeLayout(product, design), [product, design, fontsTick]);
  const [products, setProducts] = useState(null);
  useEffect(() => { api.get("/admin/products").then((r) => setProducts(r.data)).catch(() => setProducts([])); }, []);
  const matches = useMemo(() => (products || []).filter((p) => p.kind !== "pad" && p.width_mm && sameSize(p, size))
    .sort((a, b) => isGom(a) - isGom(b) || !a.active - !b.active || !(a.price > 0) - !(b.price > 0)), [products, size]);

  useEffect(() => {
    if (!sid) return;
    api.get(`/admin/saved-stamps/${sid}`).then(({ data }) => {
      setSelected(null);
      setSize({ shape: data.shape, w: data.width_mm, h: data.height_mm });
      setDesign(data.design);
      setLoaded(data);
      toast.success(`Timbro "${data.name}" aperto`);
    }).catch((e) => toast.error(errMsg(e)));
  }, [sid]);

  const pick = (s) => {
    setSelected(null);
    setSize({ shape: s.shape, w: s.w, h: s.shape === "round" ? s.w : s.h });
    setDesign((d) => ({ ...d, lines: d.lines.map((l) => (s.shape === "round" || !l.curve ? l : { ...l, curve: null })) }));
  };
  const empty = !design.lines.some((l) => l.text.trim()) && !design.graphics.length;
  const [wo, setWo] = useState(null);
  const [woTypes, setWoTypes] = useState([]);
  useEffect(() => { api.get("/admin/work-orders/settings").then((r) => setWoTypes(r.data.types)).catch(() => {}); }, []);
  const toWorkOrder = () => {
    if (empty) return toast.error("Il timbro è vuoto");
    const p = matches[0];
    const sz = round ? `Ø ${size.w} mm` : `${size.h}×${size.w} mm`;
    const text = design.lines.filter((l) => l.text.trim() && !l.pristine).map((l) => l.text).join("\n");
    setWo({ product_type: woTypes.find((t) => t.toLowerCase() === "timbro") || "Timbro", qty: 1, price: p?.price ?? "", due_date: "",
      description: `Modello: ${p?.name || "misura libera"} · ${sz}${loaded?.name ? ` · "${loaded.name}"` : ""}\nTesto:\n${text || "(solo grafica)"}`,
      stamp: { width_mm: size.w, height_mm: size.h, shape: size.shape, product_name: p?.name || "", product_id: p?.id || null, design: cleanDesign(design), layout: computeLayout(product, cleanDesign(design)) } });
  };
  const save = async (name, product_id, quantity) => {
    if (empty) return toast.error("Il timbro è vuoto");
    try {
      const { data } = await api.post("/admin/saved-stamps", { name, width_mm: size.w, height_mm: size.h, shape: size.shape, design, product_id, quantity });
      setLoaded(data);
      toast.success(`Timbro "${data.name}" salvato`);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const download = async (fmt) => {
    if (empty) return toast.error("Il timbro è vuoto");
    setBusy(fmt);
    try {
      const base = loaded?.name ? loaded.name.replace(/[^\w\-]+/g, "_") : round ? `timbro-D${size.w}mm` : `timbro-${size.h}x${size.w}mm`;
      await downloadFile({ url: "/admin/free-stamp/file", method: "post", data: { width_mm: size.w, height_mm: size.h, shape: size.shape, fmt, layout } }, fmt.startsWith("laser") ? `${base}-laser.${fmt.slice(6)}` : `${base}.${fmt}`);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };
  const tabBtn = (id, t, Icon) => (
    <button data-testid={`free-tab-${id}`} onClick={() => setTab(id)} className={`press flex-1 h-10 text-sm font-semibold flex items-center justify-center gap-1.5 border-b-2 ${tab === id ? "border-brand-yellow text-brand-navy" : "border-transparent text-slate-500"}`}><Icon className="w-4 h-4" /> {t}</button>
  );
  const dlBtn = (fmt, cls) => (
    <button data-testid={`free-download-${fmt}-btn`} disabled={!!busy} onClick={() => download(fmt)} className={`press h-10 px-4 rounded-md text-sm font-bold flex items-center gap-2 disabled:opacity-60 ${cls}`}>
      {busy === fmt ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />} {fmt.startsWith("laser") ? `Laser ${fmt.slice(6).toUpperCase()}` : `${fmt.toUpperCase()} in scala`}
    </button>
  );

  return (
    <div className="space-y-4" data-testid="admin-stamp-creator">
      <div>
        <h1 className="text-4xl font-extrabold uppercase">Crea timbro</h1>
        <p className="text-sm text-slate-500">Componi un timbro di qualsiasi misura, senza limite di righe, e scarica il file di produzione in scala 1:1.</p>
      </div>
      <SaveBar loaded={loaded} onSave={save} matches={matches} />
      <button data-testid="free-create-work-order-btn" onClick={toWorkOrder} className="press h-11 px-5 rounded-md bg-violet-700 text-white font-bold flex items-center gap-2"><ClipboardList className="w-5 h-5" /> Crea ordine in lavorazione</button>
      <WorkOrderDialog order={wo} types={woTypes} onClose={() => setWo(null)} onSaved={(o) => { if (o) setWo(null); }} />
      <div className="flex flex-wrap items-end gap-2">
        <div className="flex flex-wrap gap-2" data-testid="free-presets">
          {PRESETS.map(([w, h]) => {
            const on = !round && size.w === w && size.h === h;
            return (
              <button key={w} data-testid={`free-preset-${h}x${w}`} onClick={() => pick({ shape: "rect", w, h })}
                className={`press h-10 px-4 rounded-lg border-2 font-mono font-bold ${on ? "bg-brand-navy text-white border-brand-navy" : "bg-white border-slate-300 hover:border-brand-navy"}`}>
                {h}×{w}
              </button>
            );
          })}
        </div>
        <span className="w-px h-8 bg-slate-300 mx-1 hidden sm:block" />
        <SizeForm size={size} onApply={pick} />
      </div>
      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_380px] items-start">
        <div className="space-y-3 min-w-0 lg:sticky lg:top-[110px]">
          <StampCanvas key={`${size.shape}-${size.w}-${size.h}`} product={product} design={design} setDesign={setDesign} layout={layout} ink="#111827"
            selected={selected} setSelected={setSelected} onWarnings={setWarnings} />
          {warnings.length > 0 && <p className="text-xs text-amber-800 bg-amber-50 border border-amber-300 rounded-md p-2" data-testid="free-overflow-warning">Alcuni elementi escono dall'area stampabile o si sovrappongono.</p>}
          <div className="flex flex-wrap gap-2">
            {dlBtn("svg", "bg-brand-yellow text-brand-navy")}
            {dlBtn("pdf", "bg-brand-navy text-white")}
            {dlBtn("laser_svg", "bg-black text-white ring-1 ring-red-500")}
            {dlBtn("laser_pdf", "bg-black text-white ring-1 ring-red-500")}
          </div>
          <p className="text-[11px] text-slate-500">Laser = specchiato, colori invertiti, contorno di taglio rosso a 0,5 mm</p>
        </div>
        <div className="rounded-xl border bg-white lg:sticky lg:top-[110px] lg:max-h-[calc(100vh-130px)] flex flex-col min-w-0">
          <div className="flex border-b px-1 shrink-0">{tabBtn("text", "Testo", Type)}{tabBtn("graphics", "Grafica", Shapes)}{tabBtn("sizes", "Misure", Ruler)}</div>
          <div className="p-3 overflow-y-auto">
            {tab === "text" ? <TextLinesPanel product={product} design={design} setDesign={setDesign} selected={selected} setSelected={setSelected} warnings={warnings} layout={layout} />
              : tab === "graphics" ? <GraphicsPanel product={product} design={design} setDesign={setDesign} setSelected={setSelected} />
                : <CatalogSizes size={size} onPick={pick} products={products} />}
          </div>
        </div>
      </div>
    </div>
  );
}
